import * as mt from './mutation-types';

import { RootState } from '../types';
import { LocationState, LocationDelegate, toStateKey } from './types';
import { ActionContext } from 'vuex';
import { CountryDTO, City, StateDTO, CityDTO } from '@/api/location/types';

type LocationContext = ActionContext<LocationState, RootState>;

export default {
  [mt.SETUP]({ commit }: LocationContext, delegate: LocationDelegate) {
    commit(mt.SETUP, { delegate });
  },
  [mt.CLEAR]({ commit }: LocationContext) {
    commit(mt.CLEAR);
  },
  [mt.FETCH_COUNTRIES]({ commit, dispatch }: LocationContext, countries: string[]) {
    commit(mt.FETCH_COUNTRIES, { countries });
    dispatch(mt.SELECT_DEFERRED);
  },
  [mt.FETCHING_COUNTRIES]({ commit }: LocationContext) {
    commit(mt.FETCHING_COUNTRIES);
  },
  [mt.SELECT_COUNTRY]({ commit, getters, state }: LocationContext, countryContext: string) {
    if (getters.isLoading) {
      commit(mt.DEFER_SELECT_COUNTRY, { countryContext });
      return;
    }
    if (getters.canSelectCountries) {
      const selectedCountry = (state.countries.some(country => country === countryContext)) ? countryContext : '';
      commit(mt.SELECT_COUNTRY, { country: selectedCountry });
      commit(mt.CLEAR_STATE);
      state.delegate!.fetchStatesByCountry(selectedCountry);
    }
    commit(mt.COMMIT_COUNTRY);
  },
  [mt.FETCH_STATES]({ commit, dispatch }: LocationContext, countryDTO: CountryDTO) {
    commit(mt.FETCH_STATES, countryDTO);
    dispatch(mt.SELECT_DEFERRED);
  },
  [mt.FETCHING_STATES]({ commit }: LocationContext) {
    commit(mt.FETCHING_STATES);
  },
  [mt.SELECT_STATE]({ commit, getters, state }: LocationContext, stateContext: string) {
    if (getters.isLoading) {
      commit(mt.DEFER_SELECT_STATE, { stateContext });
      return;
    }
    if (getters.canSelectStates) {
      const selectedState = (state.statesOptions.some(st => st.acronym === stateContext)) ? stateContext : '';
      commit(mt.SELECT_STATE, { state: selectedState });
      commit(mt.CLEAR_CITY);
      state.delegate!.fetchCitiesByCountryAndState(state.countryContext, selectedState);
    }
    commit(mt.COMMIT_STATE);
  },
  [mt.FETCHING_CITIES]({ commit }: LocationContext) {
    commit(mt.FETCHING_CITIES);
  },
  [mt.FETCH_CITIES]({ commit, dispatch }: LocationContext, payload: { country: string, state: string, cities: City[] }) {
    commit(mt.FETCH_CITIES, payload);
    dispatch(mt.SELECT_DEFERRED);
  },
  [mt.SELECT_CITY]({ commit, getters, state }: LocationContext, cityContext: City) {
    if (getters.isLoading) {
      commit(mt.DEFER_SELECT_CITY, { cityContext });
      return;
    }
    if (getters.canSelectCities) {
      const selectedCity = state.citiesOptions.some(city => city === cityContext) ? cityContext : '';
      commit(mt.SELECT_CITY, { city: selectedCity });
    }
    commit(mt.COMMIT_CITY);
  },
  [mt.SELECT_DEFERRED]({ dispatch, state }: LocationContext) {
    if (!!state.deferred.country) {
      dispatch(mt.SELECT_COUNTRY, state.deferred.country);
    } else if (!!state.deferred.state) {
      dispatch(mt.SELECT_STATE, state.deferred.state);
    } else if (!!state.deferred.city) {
      dispatch(mt.SELECT_CITY, state.deferred.city);
    }
  },
  [mt.FETCH_CITY_CENTROID]({ commit }: LocationContext, cityDTO: CityDTO) {
    commit(mt.FETCH_CITY_CENTROID, cityDTO);
  },
  [mt.FETCHING_CITY_CENTROID]({ commit }: LocationContext) {
    commit(mt.FETCHING_CITY_CENTROID);
  },
};
